import { fhirClients } from "src-next/shared/api/fhirClient";
import { extractResourcesFromBundle } from "src-next/modules/crm-clinical/statistics/helpers/crmMessageFranchiseStatsHelpers";

import { SNCR_BASIC } from "../constants";
import {
  parseSncrNumeracaoBasic,
  serializeLotes,
  uniqueConcatNumeros,
  upsertExtension,
  type SncrNumeracaoBloco,
  type SncrNumeracaoLote,
} from "../helpers/basicExtensions";
import type {
  SncrConselho,
  SncrNotificacaoTipo,
  SncrNumeracaoBlocoStatus,
  SncrNumeracaoFaixa,
} from "../types";

const EXT = SNCR_BASIC.extensions;

export type CreateSncrNumeracaoBasicParams = {
  faixa: SncrNumeracaoFaixa;
  tipoReceita: SncrNotificacaoTipo;
  conselho: SncrConselho;
  uf: string;
  documento: string;
  saldoReceitas?: number;
  mensagemApi?: string;
  practitionerReference?: string;
  organizationReference?: string;
  status?: SncrNumeracaoBlocoStatus;
};

function extString(url: string, value: string): fhir5.Extension {
  return { url, valueString: value };
}

function extCode(url: string, value: string): fhir5.Extension {
  return { url, valueCode: value };
}

function extInteger(url: string, value: number): fhir5.Extension {
  return { url, valueInteger: value };
}

function buildLoteFromFaixa(
  faixa: SncrNumeracaoFaixa,
  mensagemApi?: string,
  created = new Date().toISOString(),
): SncrNumeracaoLote {
  return {
    inicio: faixa.inicio,
    fim: faixa.fim,
    quantidade: faixa.quantidade,
    numeros: [...faixa.numeros],
    created,
    ...(mensagemApi?.trim() ? { mensagemApi: mensagemApi.trim() } : {}),
  };
}

function buildConsolidatedFields(lotes: SncrNumeracaoLote[], usados: string[]) {
  const numeros = lotes.reduce<string[]>(
    (acc, lote) => uniqueConcatNumeros(acc, lote.numeros),
    [],
  );
  const usadosSet = new Set(usados);
  const disponiveis = numeros.filter((n) => !usadosSet.has(n));
  const status: SncrNumeracaoBlocoStatus =
    disponiveis.length === 0 ? "esgotado" : "disponivel";

  return {
    numeros,
    inicio: numeros[0] || lotes[0]?.inicio || "",
    fim: numeros[numeros.length - 1] || lotes[lotes.length - 1]?.fim || "",
    quantidade: numeros.length,
    status,
  };
}

function applyExtensions(params: {
  tipoReceita: SncrNotificacaoTipo;
  conselho: string;
  uf: string;
  documento: string;
  numeros: string[];
  usados: string[];
  inicio: string;
  fim: string;
  quantidade: number;
  status: SncrNumeracaoBlocoStatus;
  lotes: SncrNumeracaoLote[];
  saldoReceitas?: number;
  mensagemApi?: string;
  base?: fhir5.Extension[];
}): fhir5.Extension[] {
  let extension = [...(params.base ?? [])];

  const next: fhir5.Extension[] = [
    extCode(EXT.tipoReceita, params.tipoReceita),
    extString(EXT.inicio, params.inicio),
    extString(EXT.fim, params.fim),
    extInteger(EXT.quantidade, params.quantidade),
    extCode(EXT.status, params.status),
    extString(EXT.conselho, params.conselho),
    extString(EXT.uf, params.uf.toUpperCase()),
    extString(EXT.documento, params.documento),
    extString(EXT.numeros, JSON.stringify(params.numeros)),
    extString(EXT.numerosUsados, JSON.stringify(params.usados)),
    extString(EXT.lotes, serializeLotes(params.lotes)),
  ];

  for (const item of next) {
    extension = upsertExtension(extension, item);
  }

  if (typeof params.saldoReceitas === "number") {
    extension = upsertExtension(extension, {
      url: EXT.saldoReceitas,
      valueInteger: params.saldoReceitas,
    });
  }

  if (params.mensagemApi?.trim()) {
    extension = upsertExtension(extension, {
      url: EXT.mensagemApi,
      valueString: params.mensagemApi.trim(),
    });
  }

  return extension;
}

export function buildSncrNumeracaoNotificacaoBasic(
  params: CreateSncrNumeracaoBasicParams,
): fhir5.Basic {
  const lote = buildLoteFromFaixa(params.faixa, params.mensagemApi);
  const lotes = [lote];
  const consolidated = buildConsolidatedFields(lotes, []);
  const status = params.status ?? consolidated.status;

  const extension = applyExtensions({
    tipoReceita: params.tipoReceita,
    conselho: params.conselho,
    uf: params.uf,
    documento: params.documento,
    numeros: consolidated.numeros,
    usados: [],
    inicio: consolidated.inicio,
    fim: consolidated.fim,
    quantidade: consolidated.quantidade,
    status,
    lotes,
    saldoReceitas: params.saldoReceitas,
    mensagemApi: params.mensagemApi,
  });

  return {
    resourceType: "Basic",
    meta: { profile: [SNCR_BASIC.profile] },
    code: {
      coding: [
        {
          system: SNCR_BASIC.codeSystem,
          code: SNCR_BASIC.code,
          display: "Numeração SNCR — Notificação de Receita",
        },
      ],
    },
    ...(params.practitionerReference
      ? { subject: { reference: params.practitionerReference } }
      : {}),
    ...(params.organizationReference
      ? { author: { reference: params.organizationReference } }
      : {}),
    created: new Date().toISOString(),
    extension,
  };
}

function pickCanonicalBasic(resources: fhir5.Basic[]): fhir5.Basic | null {
  if (!resources.length) return null;

  return [...resources].sort((a, b) => {
    const ta = a.meta?.lastUpdated || a.created || "";
    const tb = b.meta?.lastUpdated || b.created || "";
    return tb.localeCompare(ta);
  })[0];
}

export async function findSncrNumeracaoBasicForTipo(params: {
  tipoReceita: SncrNotificacaoTipo;
  practitionerReference?: string;
}): Promise<fhir5.Basic | null> {
  const searchParams: Record<string, string | number> = {
    code: `${SNCR_BASIC.codeSystem}|${SNCR_BASIC.code}`,
    _sort: "-_lastUpdated",
    _count: 100,
  };

  if (params.practitionerReference) {
    searchParams.subject = params.practitionerReference;
  }

  const bundle = await fhirClients.admin.search<fhir5.Basic>(
    "Basic",
    searchParams,
  );


  const resources = extractResourcesFromBundle<fhir5.Basic>(bundle).filter(
    (resource) => {
      const parsed = parseSncrNumeracaoBasic(resource);
      return parsed?.tipoReceita === params.tipoReceita;
    },
  );

  return pickCanonicalBasic(resources);
}

export async function upsertSncrNumeracaoNotificacaoBasic(
  params: CreateSncrNumeracaoBasicParams,
): Promise<fhir5.Basic> {
  const existing = await findSncrNumeracaoBasicForTipo({
    tipoReceita: params.tipoReceita,
    practitionerReference: params.practitionerReference,
  });

  if (!existing?.id) {
    return createSncrNumeracaoNotificacaoBasic(params);
  }

  const fresh = await fhirClients.admin.get<fhir5.Basic>("Basic", existing.id);
  const parsed = parseSncrNumeracaoBasic(fresh);
  if (!parsed) {
    throw new Error(
      "Basic SNCR inválido (sem sncr-lotes). Solicite novamente a numeração.",
    );
  }

  const novoLote = buildLoteFromFaixa(params.faixa, params.mensagemApi);
  const lotes = [...parsed.lotes, novoLote];
  const usados = parsed.usados;
  const consolidated = buildConsolidatedFields(lotes, usados);

  const extension = applyExtensions({
    tipoReceita: params.tipoReceita,
    conselho: params.conselho,
    uf: params.uf,
    documento: params.documento,
    numeros: consolidated.numeros,
    usados,
    inicio: consolidated.inicio,
    fim: consolidated.fim,
    quantidade: consolidated.quantidade,
    status: consolidated.status,
    lotes,
    saldoReceitas: params.saldoReceitas,
    mensagemApi: params.mensagemApi,
    base: fresh.extension,
  });

  return fhirClients.admin.update<fhir5.Basic>("Basic", existing.id, {
    ...fresh,
    ...(params.practitionerReference
      ? { subject: { reference: params.practitionerReference } }
      : {}),
    ...(params.organizationReference
      ? { author: { reference: params.organizationReference } }
      : {}),
    extension,
  });
}

export async function createSncrNumeracaoNotificacaoBasic(
  params: CreateSncrNumeracaoBasicParams,
): Promise<fhir5.Basic> {
  const resource = buildSncrNumeracaoNotificacaoBasic(params);
  return fhirClients.admin.create<fhir5.Basic>("Basic", resource);
}

export async function listSncrNumeracaoBlocos(params?: {
  practitionerReference?: string;
}): Promise<SncrNumeracaoBloco[]> {
  const searchParams: Record<string, string | number> = {
    code: `${SNCR_BASIC.codeSystem}|${SNCR_BASIC.code}`,
    _sort: "-_lastUpdated",
    _count: 100,
  };

  if (params?.practitionerReference) {
    searchParams.subject = params.practitionerReference;
  }

  const bundle = await fhirClients.admin.search<fhir5.Basic>(
    "Basic",
    searchParams,
  );

  return extractResourcesFromBundle<fhir5.Basic>(bundle)
    .map(parseSncrNumeracaoBasic)
    .filter((item): item is SncrNumeracaoBloco => Boolean(item));
}

export async function markSncrNumeroUsado(params: {
  basicId: string;
  numero: string;
}): Promise<SncrNumeracaoBloco> {
  const existing = await fhirClients.admin.get<fhir5.Basic>(
    "Basic",
    params.basicId,
  );
  const parsed = parseSncrNumeracaoBasic(existing);
  if (!parsed) {
    throw new Error("Bloco de numeração SNCR inválido.");
  }
  if (!parsed.numeros.includes(params.numero)) {
    throw new Error("Número não pertence a este bloco.");
  }
  if (parsed.usados.includes(params.numero)) {
    return parsed;
  }

  const usados = [...parsed.usados, params.numero];
  const disponiveis = parsed.numeros.filter((n) => !usados.includes(n));
  const status: SncrNumeracaoBlocoStatus =
    disponiveis.length === 0 ? "esgotado" : "disponivel";

  let extension = upsertExtension(existing.extension, {
    url: EXT.numerosUsados,
    valueString: JSON.stringify(usados),
  });
  extension = upsertExtension(extension, {
    url: EXT.status,
    valueCode: status,
  });

  const updated = await fhirClients.admin.update<fhir5.Basic>("Basic", params.basicId, {
    ...existing,
    extension,
  });

  const result = parseSncrNumeracaoBasic(updated);
  if (!result) {
    throw new Error("Falha ao interpretar bloco após atualização.");
  }
  return result;
}

export type ClaimedSncrNumero = {
  basicId: string;
  numero: string;
  tipo: SncrNotificacaoTipo;
};

export async function peekNextSncrNumero(params: {
  tipo: SncrNotificacaoTipo;
  practitionerReference?: string;
  excludeNumeros?: string[];
}): Promise<ClaimedSncrNumero> {
  const exclude = new Set(
    (params.excludeNumeros ?? []).filter((n) => !!n && String(n).length > 0),
  );

  const blocos = await listSncrNumeracaoBlocos({
    practitionerReference: params.practitionerReference,
  });

  const bloco = blocos.find(
    (b) =>
      b.tipoReceita === params.tipo &&
      b.status !== "esgotado" &&
      b.disponiveis.some((n) => !exclude.has(n)),
  );

  if (!bloco?.id) {
    throw new Error(
      `Sem numeração SNCR disponível para ${params.tipo}. Solicite um lote antes de emitir.`,
    );
  }

  const numero = bloco.disponiveis.find((n) => !exclude.has(n));
  if (!numero) {
    throw new Error(
      `Sem numeração SNCR disponível para ${params.tipo}. Solicite um lote antes de emitir.`,
    );
  }

  return {
    basicId: bloco.id,
    numero,
    tipo: params.tipo,
  };
}

export async function claimNextSncrNumero(params: {
  tipo: SncrNotificacaoTipo;
  practitionerReference?: string;
  excludeNumeros?: string[];
}): Promise<ClaimedSncrNumero> {
  const peeked = await peekNextSncrNumero(params);
  await markSncrNumeroUsado({
    basicId: peeked.basicId,
    numero: peeked.numero,
  });
  return peeked;
}

export async function claimSncrNumeroOrNext(params: {
  tipo: SncrNotificacaoTipo;
  basicId?: string;
  numero?: string;
  practitionerReference?: string;
  excludeNumeros?: string[];
}): Promise<ClaimedSncrNumero> {
  if (params.basicId && params.numero) {
    const blocos = await listSncrNumeracaoBlocos({
      practitionerReference: params.practitionerReference,
    });
    const bloco = blocos.find((b) => b.id === params.basicId);
    if (bloco?.disponiveis.includes(params.numero)) {
      await markSncrNumeroUsado({
        basicId: params.basicId,
        numero: params.numero,
      });
      return {
        basicId: params.basicId,
        numero: params.numero,
        tipo: params.tipo,
      };
    }
  }

  return claimNextSncrNumero({
    tipo: params.tipo,
    practitionerReference: params.practitionerReference,
    excludeNumeros: params.excludeNumeros,
  });
}

export async function burnSncrNumeroAfterEmit(params: {
  basicId: string;
  numero: string;
  tipo: SncrNotificacaoTipo;
  practitionerReference?: string;
}): Promise<ClaimedSncrNumero> {
  const existing = await fhirClients.admin.get<fhir5.Basic>(
    "Basic",
    params.basicId,
  );
  const parsed = parseSncrNumeracaoBasic(existing);

  if (parsed?.usados.includes(params.numero)) {
    return {
      basicId: params.basicId,
      numero: params.numero,
      tipo: params.tipo,
    };
  }

  if (parsed?.numeros.includes(params.numero)) {
    await markSncrNumeroUsado({
      basicId: params.basicId,
      numero: params.numero,
    });
    return {
      basicId: params.basicId,
      numero: params.numero,
      tipo: params.tipo,
    };
  }

  return claimNextSncrNumero({
    tipo: params.tipo,
    practitionerReference: params.practitionerReference,
  });
}

export async function unmarkSncrNumeroUsado(params: {
  basicId: string;
  numero: string;
}): Promise<SncrNumeracaoBloco> {
  const existing = await fhirClients.admin.get<fhir5.Basic>(
    "Basic",
    params.basicId,
  );
  const parsed = parseSncrNumeracaoBasic(existing);
  if (!parsed) {
    throw new Error("Bloco de numeração SNCR inválido.");
  }

  const usados = parsed.usados.filter((n) => n !== params.numero);
  const disponiveis = parsed.numeros.filter((n) => !usados.includes(n));
  const status: SncrNumeracaoBlocoStatus =
    disponiveis.length === 0 ? "esgotado" : "disponivel";

  let extension = upsertExtension(existing.extension, {
    url: EXT.numerosUsados,
    valueString: JSON.stringify(usados),
  });
  extension = upsertExtension(extension, {
    url: EXT.status,
    valueCode: status,
  });

  const updated = await fhirClients.admin.update<fhir5.Basic>("Basic", params.basicId, {
    ...existing,
    extension,
  });

  const result = parseSncrNumeracaoBasic(updated);
  if (!result) {
    throw new Error("Falha ao interpretar bloco após atualização.");
  }
  return result;
}
